import { observer } from "mobx-react";
import React, { useMemo, useState } from "react";
import { Box, Grid, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import AcxExpansion from "components/UI/AcxExpansion";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import moment from "moment";
import { AsyncTaskStatus } from "stores/BaseStore";
import { useStore } from "utils/useStore";
import { addtlMetadataCatTitle } from "./ManageFilterInputDrawer";
import AudioMetadataModel from "models/AudioMetadataModel";
import { startCase } from "lodash";

const GrayColorGrid = styled(Grid)({ color: "#71717A" });
const ValueGrid = styled(Grid)({
    whiteSpace: "normal",
    wordBreak: "break-all",
});

export const FocusedAdditionalMetadata = observer(() => {
    const store = useStore(ConversationsStore);

    // Using the ConversationsStore filter categories we get the
    // actual labels for meta values 1-5
    const metaLabels = useMemo(
        () =>
            store.allFilterCategories.find(
                (category) => category.title === addtlMetadataCatTitle,
            )?.filters,
        [store],
    );

    const [metadata, setMetadata] = useState<
        | {
              audioMetadata: AudioMetadataModel;
              extendedMetadata: Record<string, unknown>;
          }
        | undefined
    >(undefined);
    const isMetadataLoading = store.getTaskLoading("SingleConvoMetadata");

    return (
        <AcxExpansion
            header={
                <Stack>
                    <Box fontWeight={"bold"} fontSize={14}>
                        Additional Metadata
                    </Box>
                </Stack>
            }
            onExpandChange={(expanded) => {
                if (metadata || isMetadataLoading) return;
                if (expanded) {
                    store
                        .getFocusedConversationAdditionalMetadata()
                        .then((metadata) => {
                            if (metadata !== AsyncTaskStatus.Error)
                                setMetadata(metadata);
                        });
                }
            }}
            conversationVariant
            body={
                <React.Fragment>
                    {metadata && (
                        <Grid container direction="column" gap={1}>
                            <Grid item container direction="column">
                                <GrayColorGrid item fontSize={14}>
                                    File Name
                                </GrayColorGrid>
                                <ValueGrid item>
                                    {
                                        metadata.audioMetadata
                                            .fileName as React.ReactNode
                                    }
                                </ValueGrid>
                            </Grid>
                            <Grid item container direction="column">
                                <GrayColorGrid item fontSize={14}>
                                    File Size (KB)
                                </GrayColorGrid>
                                <ValueGrid item>
                                    {
                                        metadata.audioMetadata
                                            .fileSizeKiloBytes as React.ReactNode
                                    }
                                </ValueGrid>
                            </Grid>
                            <Grid item container direction="column">
                                <GrayColorGrid item fontSize={14}>
                                    Arrival Date
                                </GrayColorGrid>
                                <ValueGrid item>
                                    {moment(
                                        metadata.audioMetadata.arrivedOn,
                                    ).format("MM/DD/YYYY")}
                                </ValueGrid>
                            </Grid>
                            {store.currentEval && (
                                <Grid item container direction="column">
                                    <GrayColorGrid item fontSize={14}>
                                        Sample Date
                                    </GrayColorGrid>
                                    <Grid
                                        item
                                        sx={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {moment(
                                            store.currentEval.createdOn,
                                        ).format("MM/DD/YYYY")}
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item container direction="column">
                                <GrayColorGrid item fontSize={14}>
                                    Client Call ID
                                </GrayColorGrid>
                                <ValueGrid item>
                                    {
                                        metadata.audioMetadata
                                            .clientCallId as React.ReactNode
                                    }
                                </ValueGrid>
                            </Grid>
                            {metaLabels &&
                                metaLabels
                                    .filter(
                                        (mapper) =>
                                            !!metadata.audioMetadata[
                                                mapper.storeObjectKey
                                            ],
                                    )
                                    .map((mapper) => (
                                        <Grid
                                            item
                                            container
                                            direction="column"
                                            key={mapper.storeObjectKey}
                                        >
                                            <GrayColorGrid item fontSize={14}>
                                                {mapper.filterLabel ??
                                                    mapper.filterTitle}
                                            </GrayColorGrid>
                                            <ValueGrid item>
                                                {
                                                    metadata.audioMetadata[
                                                        mapper.storeObjectKey
                                                    ] as React.ReactNode
                                                }
                                            </ValueGrid>
                                        </Grid>
                                    ))}
                            {Object.entries(metadata.extendedMetadata).map(
                                ([key, value]) => (
                                    <Grid
                                        item
                                        container
                                        direction="column"
                                        key={key}
                                    >
                                        <GrayColorGrid item fontSize={14}>
                                            {startCase(key)}
                                        </GrayColorGrid>
                                        <ValueGrid item>
                                            {value as React.ReactNode}
                                        </ValueGrid>
                                    </Grid>
                                ),
                            )}
                        </Grid>
                    )}
                    {(!metadata || isMetadataLoading) && (
                        <AcxLoadingIndicator
                            color="secondary"
                            alternate="PuffLoader"
                            size={32}
                        />
                    )}
                </React.Fragment>
            }
        />
    );
});
